<template>
  <el-form ref="elFormRef" :label-width="80" :model="formData" :rules="formRules">
    <el-form-item label="奖励项目" prop="name">
      <el-input v-model="formData.name" clearable maxlength="20" placeholder="请输入奖励项目" show-word-limit />
    </el-form-item>
    <el-form-item label="控制条件" prop="ruleType" required>
      <el-radio-group v-model="formData.ruleType">
        <el-row v-for="item in controlOptions" :key="item.value" :class="item.value !== 'CUMULATIVE' ? 'mb-10' : ''">
          <el-radio v-if="item.value === 'NORMAL'" :label="item.value">
            入职时长
            <el-input-number v-model="item.ruleDayValue" v-bind="controlInputNumberProps" />
            天
          </el-radio>
          <el-radio v-else-if="item.value === 'URGENT'" :label="item.value">
            急招 入职满
            <el-input-number v-model="item.ruleDayValue" v-bind="controlInputNumberProps" />
            天
          </el-radio>
          <el-radio v-else :label="item.value">
            累计推荐
            <el-input-number v-model="item.rulePeopleValue" v-bind="controlInputNumberProps" />
            人，入职满
            <el-input-number v-model="item.ruleDayValue" v-bind="controlInputNumberProps" />
            天
            <el-tooltip
              content="1.此条件为机构内全局控制条件，创建后不可编辑，需先下架后才能删除。2.及新增加此控制条件。"
              placement="right"
              style="margin-left: 8px; transform: translateY(4px)"
            >
              <el-icon color="#999" :size="16"><InfoFilled /></el-icon>
              <template #content>
                <p>1.此条件为机构内全局控制条件，创建后不可编辑，需先下架后才能删除。</p>
                <p>2.及新增加此控制条件。</p>
              </template>
            </el-tooltip>
          </el-radio>
        </el-row>
      </el-radio-group>
    </el-form-item>
    <el-form-item label="有效期" prop="effective">
      <el-date-picker
        v-model="formData.effective"
        :disabled="formData.timeIsVerified"
        end-placeholder="结束时间"
        format="YYYY-MM-DD"
        range-separator="至"
        start-placeholder="开始时间"
        type="daterange"
        value-format="YYYY-MM-DD"
      />
      <el-checkbox v-model="formData.timeIsVerified" class="ml-20">长期有效</el-checkbox>
    </el-form-item>
    <el-form-item label="奖励金额" prop="rewardAmount">
      <el-input-number
        v-model="formData.rewardAmount"
        :controls="false"
        :min="0"
        placeholder="请输入奖励金额"
        :precision="2"
        style="width: 172px"
      />
      元
    </el-form-item>
    <el-form-item label="状态" prop="status">
      <el-select v-model="formData.status">
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :disabled="item.disabled"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
    </el-form-item>
    <el-form-item label="备注" prop="remark">
      <el-input
        v-model="formData.remark"
        clearable
        maxlength="50"
        placeholder="请输入备注"
        show-word-limit
        type="textarea"
      />
    </el-form-item>
  </el-form>
</template>

<script setup>
  import { ref, reactive, defineExpose, defineEmits, toRaw, getCurrentInstance, onMounted } from 'vue'
  import { debounce } from 'lodash'
  import { addReward, updateReward } from '@/api/recruitment'
  import { InfoFilled } from '@element-plus/icons'
  import dayjs from 'dayjs'

  const { proxy } = getCurrentInstance()

  const controlInputNumberProps = {
    size: 'mini',
    min: 0,
    precision: 0,
    controls: false,
    style: 'width: 80px;',
  }

  const controlOptions = ref([
    { label: '入职时长', value: 'NORMAL', ruleDayValue: 0 },
    { label: '急招', value: 'URGENT', ruleDayValue: 0 },
    { label: '累计推荐', value: 'CUMULATIVE', rulePeopleValue: 0, ruleDayValue: 0 },
  ])

  const statusOptions = [
    { label: '启用', value: 'ENABLED' },
    { label: '下架', value: 'CLOSED' },
  ]

  const props = defineProps({
    data: {
      type: Object,
      default: () => ({}),
    },
  })

  const emit = defineEmits(['success', 'close'])

  const formData = reactive({
    name: '',
    ruleType: '',
    ruleDayValue: 0,
    rulePeopleValue: 0,
    timeIsVerified: false,
    effective: [],
    effectiveFrom: '',
    effectiveTo: '',
    rewardAmount: 0,
    status: 'ENABLED',
    remark: '',
  })

  onMounted(() => {
    for (const key in props.data) {
      if (['timeIsVerified', 'effectiveFrom', 'effectiveTo'].includes(key)) {
        initTime()
      } else if (['ruleType', 'ruleDayValue', 'rulePeopleValue'].includes(key)) {
        initControl()
      } else {
        formData[key] = props.data[key]
      }
    }
  })

  const initTime = debounce(() => {
    let { timeIsVerified, effectiveFrom, effectiveTo } = props.data
    formData.timeIsVerified = timeIsVerified
    if (!timeIsVerified) {
      effectiveFrom = dayjs(effectiveFrom).format('YYYY-MM-DD')
      effectiveTo = dayjs(effectiveTo).format('YYYY-MM-DD')
      formData.effectiveFrom = effectiveFrom
      formData.effectiveTo = effectiveTo
      formData.effective = [effectiveFrom, effectiveTo]
    }
  }, 50)

  const initControl = debounce(() => {
    const { ruleType, ruleDayValue, rulePeopleValue } = props.data
    formData.ruleType = ruleType
    const item = controlOptions.value.find((e) => e.value === ruleType)
    if (!item) return
    item.ruleDayValue = ruleDayValue
    if (ruleType === 'CUMULATIVE') item.rulePeopleValue = rulePeopleValue
  }, 50)

  const formRules = {
    name: [{ required: true, message: '请填写奖励项目' }],
    ruleType: [
      {
        required: true,
        message: '请选择控制条件',
      },
    ],
    effective: [
      {
        required: true,
        message: '请填写有效期',
        validator(rule, value, callback) {
          let msg = ''
          if (!formData.timeIsVerified) {
            const [start, end] = formData.effective
            if (!start || !end) msg = '请填写正确的有效期'
          }
          callback(msg ? new Error(msg) : void 0)
        },
      },
    ],
    rewardAmount: [
      {
        required: true,
        message: '请填写奖励金额',
      },
      {
        validator(rule, value, callback) {
          callback(value > 0 ? void 0 : new Error('奖励金额必须大于0'))
        },
      },
    ],
    status: [{ required: true, message: '请选择状态' }],
  }

  function checkControl() {
    const value = formData.ruleType
    const { rulePeopleValue } = controlOptions.value.find((e) => e.value === value)
    let msg = ''
    if (value === 'CUMULATIVE' && !rulePeopleValue) msg = '累计推荐人数不能为0'
    return msg
  }

  const elFormRef = ref()
  function submit() {
    elFormRef.value.validate((valid) => {
      if (!valid) return

      const msg = checkControl()
      if (msg) return proxy.$baseMessage(msg, 'error', 'vab-hey-message-error')

      const params = toRaw(formData)

      if (params.timeIsVerified) {
        params.effectiveFrom = ''
        params.effectiveTo = ''
      } else {
        const [start, end] = params.effective
        params.effectiveFrom = start
        params.effectiveTo = end + ' 23:59:59'
      }
      Reflect.deleteProperty(params, 'effective')

      const currentControlItem = controlOptions.value.find((e) => e.value === params.ruleType)
      params.ruleDayValue = currentControlItem.ruleDayValue
      if (params.ruleType === 'CUMULATIVE') params.rulePeopleValue = currentControlItem.rulePeopleValue

      let fn = params.id ? updateReward : addReward

      fn(params).then(() => {
        proxy.$baseMessage(`处理成功！`, 'success', 'vab-hey-message-success')
        emit('success', params.id ? 'update' : 'add')
      })
    })
  }

  defineExpose({
    submit,
  })
</script>

<style lang="scss" scoped>
  .el-radio :deep(.el-radio__input + .el-radio__label),
  .el-checkbox :deep(.el-checkbox__input + .el-checkbox__label) {
    margin-left: 0;
  }
</style>
